import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import {
    actualizarTurno,
    proponerCambioFecha,
    aceptarCambioFecha,
    rechazarCambioFecha,
    asignarPlanAPaciente,
    getPlanes,
    getObrasSociales,
} from "../../../api/apiResources";
import PacienteSidebar from "../../../components/sidebars/paciente/PacienteSidebar";
import NotificacionesBell from "../../../components/notificaciones/NotificacionesBell";
import { NotificacionesProvider } from "../../../components/notificaciones/NotificacionesContext";
import BuscarTurno from "../../turnos/buscarTurno/BuscarTurno";
import MisTurnos from "../../turnos/MisTurnos";
import Directorio from "./Directorio";
import {
    getEntityId,
    normalizeTurno,
    normalizeMedico,
    normalizeSede,
    normalizeServicio,
    resolvePaciente,
} from "./patientHelpers";
import "./PatientLayout.css";

const PatientLayout = ({ user, onLogout }) => {
    const { page } = useParams();
    const [activePage, setActivePage] = useState(page || "turnos");
    const [turnos, setTurnos] = useState([]);
    const [medicos, setMedicos] = useState([]);
    const [sedes, setSedes] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [obrasSociales, setObrasSociales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pacienteActual, setPacienteActual] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [
                pacientesRes,
                turnosRes,
                medicosRes,
                sedesRes,
                serviciosRes,
                planesData,
                osData,
            ] = await Promise.all([
                apiClient.get("/pacientes"),
                apiClient.get("/turnos/buscar?limit=500"),
                apiClient.get("/medicos"),
                apiClient.get("/sedes"),
                apiClient.get("/servicios"),
                getPlanes(),
                getObrasSociales(),
            ]);

            const pacientesData = Array.isArray(pacientesRes?.data) ? pacientesRes.data : (Array.isArray(pacientesRes) ? pacientesRes : []);
            const turnosData = Array.isArray(turnosRes?.data) ? turnosRes.data : (Array.isArray(turnosRes) ? turnosRes : []);
            const medicosData = Array.isArray(medicosRes?.data) ? medicosRes.data : (Array.isArray(medicosRes) ? medicosRes : []);
            const sedesData = Array.isArray(sedesRes?.data) ? sedesRes.data : (Array.isArray(sedesRes) ? sedesRes : []);
            const serviciosData = Array.isArray(serviciosRes?.data) ? serviciosRes.data : (Array.isArray(serviciosRes) ? serviciosRes : []);

            const matchedPaciente = resolvePaciente(pacientesData, user);
            setPacienteActual(matchedPaciente);

            const matchedPacienteId = getEntityId(matchedPaciente);

            const normalizedTurnos = turnosData
                .filter((turno) => {
                    if (!turno) return false;
                    const estado = String(turno.estado || "").toLowerCase();
                    const pacienteId = getEntityId(turno.paciente);
                    if (estado === "disponible") return true;
                    return pacienteId === matchedPacienteId;
                })
                .map(normalizeTurno);

            setTurnos(normalizedTurnos);
            setMedicos(medicosData.map(normalizeMedico));
            setSedes(sedesData.map((sede) => normalizeSede(sede, medicosData)));
            setServicios(serviciosData.map((servicio) => normalizeServicio(servicio, medicosData)));
            setPlanes(Array.isArray(planesData) ? planesData.map((p) => ({ ...p, id: p._id || p.id })) : []);
            setObrasSociales(Array.isArray(osData) ? osData.map((os) => ({ ...os, id: os._id || os.id })) : []);
        } catch (err) {
            console.error("Error cargando datos del paciente:", err);
            setError("No se pudieron cargar los datos. Por favor, intente nuevamente más tarde.");
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (page) setActivePage(page);
    }, [page]);

    const handleTurnoModificado = () => loadData();

    const handleCancelar = async (turno) => {
        await actualizarTurno(turno.id, { estado: "CANCELADO", id_paciente: getEntityId(pacienteActual) });
        loadData();
    };

    const handleProponerCambio = async (turno, nuevaFecha) => {
        await proponerCambioFecha(turno.id, nuevaFecha, null, getEntityId(pacienteActual));
        loadData();
    };

    const handleAceptarCambio = async (turno) => {
        await aceptarCambioFecha(turno.id, null, getEntityId(pacienteActual));
        loadData();
    };

    const handleRechazarCambio = async (turno) => {
        await rechazarCambioFecha(turno.id, null, getEntityId(pacienteActual));
        loadData();
    };

    const handleContratarPlan = async (planId) => {
        const pacienteId = getEntityId(pacienteActual);
        if (!pacienteId) throw new Error("No se pudo identificar al paciente.");
        await asignarPlanAPaciente(pacienteId, planId);
        await loadData();
    };

    return (
        <NotificacionesProvider user={user}>
            <div className="patient-layout">
                <NotificacionesBell />
                <PacienteSidebar currentPage={activePage} onNavigate={setActivePage} user={user} onLogout={onLogout} />
                <main id="patient-main-content" className="patient-layout__main" tabIndex={-1} aria-label="Contenido principal">
                    {activePage === "turnos" && (
                        <MisTurnos
                            turnos={turnos.filter((t) => t.pacienteId === getEntityId(pacienteActual))}
                            loading={loading}
                            error={error}
                            paciente={pacienteActual}
                            onCancelar={handleCancelar}
                            onProponerCambio={handleProponerCambio}
                            onAceptarCambio={handleAceptarCambio}
                            onRechazarCambio={handleRechazarCambio}
                            onTurnoModificado={handleTurnoModificado}
                        />
                    )}
                    {activePage === "buscar" && (
                        <BuscarTurno
                            paciente={pacienteActual}
                            onReservar={handleTurnoModificado}
                        />
                    )}
                    {activePage === "directorio" && (
                        <Directorio
                            medicos={medicos}
                            sedes={sedes}
                            servicios={servicios}
                            planes={planes}
                            obrasSociales={obrasSociales}
                            paciente={pacienteActual}
                            loading={loading}
                            error={error}
                            onContratarPlan={handleContratarPlan}
                        />
                    )}
                </main>
            </div>
        </NotificacionesProvider>
    );
};

export default PatientLayout;